import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Plus, Star } from "lucide-react";
import { useWorld } from "@/context/WorldContext";
import { useOrigin } from "@/components/Origin";
import { SHIP_STATUSES, STATUS_ORDER } from "@/lib/constants";
import { shipLabel, shipMemberIds } from "@/lib/infobox";
import { ChartCell, ColHead, RowHead } from "@/components/ShipChart";

const pairKey = (a, b) => [a, b].sort().join("|");

const useChart = (pages) => useMemo(() => {
  const byId = Object.fromEntries(pages.filter((p) => p.category === "Characters").map((c) => [c.id, c]));
  const ships = pages.filter((p) => p.category === "Ships");
  const cells = {};
  ships.forEach((ship) => {
    const members = shipMemberIds(ship).filter((id) => byId[id]);
    members.forEach((a, i) => members.slice(i + 1).forEach((b) => {
      const otp = (ship.otp_by || []).some((id) => id === a || id === b);
      (cells[pairKey(a, b)] ||= []).push({ ship, members, otp });
    }));
  });
  Object.values(cells).forEach((list) => list.sort((x, y) => STATUS_ORDER.indexOf(shipLabel(x.ship)) - STATUS_ORDER.indexOf(shipLabel(y.ship))));
  const ids = new Set(Object.keys(cells).flatMap((k) => k.split("|")));
  const chars = [...ids].map((id) => byId[id]).sort((x, y) => x.title.localeCompare(y.title));
  return { byId, ships, cells, chars };
}, [pages]);

const Filters = ({ hidden, setHidden, ships }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid="chart-status-filters">
    {STATUS_ORDER.map((s) => {
      const off = hidden.includes(s);
      return (
        <button key={s} onClick={() => setHidden((h) => (off ? h.filter((x) => x !== s) : [...h, s]))} aria-pressed={!off} data-testid={`chart-filter-${s.toLowerCase()}`}
          className={`flex items-center gap-1.5 text-xs font-code uppercase tracking-wider rounded-full border px-3 py-1 transition-[opacity,background-color] ${off ? "opacity-40 border-line text-slate-500 line-through" : SHIP_STATUSES[s].cls}`}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: SHIP_STATUSES[s].hex }} />{s}
          <span className="opacity-60">{ships.filter((x) => shipLabel(x) === s).length}</span>
        </button>
      );
    })}
    <span className="text-[11px] font-code text-slate-500 flex items-center gap-3 ml-1" data-testid="chart-legend">
      <span className="flex items-center gap-1"><Star className="w-3 h-3 text-amber-300 fill-amber-300" /> OTP</span>
      <span>+ poly ship</span>
    </span>
  </div>
);

const Empty = ({ title, body }) => {
  const { basePath, readOnly } = useWorld();
  return (
    <div className="rounded-2xl border border-dashed border-pink-500/30 p-14 text-center" data-testid="chart-empty">
      <Heart className="w-8 h-8 text-pink-400/70 mx-auto mb-3" />
      <p className="font-display text-3xl text-amber-100">{title}</p>
      <p className="text-slate-500 mt-2">{body}</p>
      {!readOnly && (
        <Link to={`${basePath}/new?category=Ships`} className="inline-flex items-center gap-1.5 mt-5 rounded-full bg-pink-500 hover:bg-pink-400 text-onaccent text-sm font-semibold px-5 py-2.5 transition-colors" data-testid="chart-create-ship-btn">
          <Plus className="w-4 h-4" /> Create a ship
        </Link>
      )}
    </div>
  );
};

export default function ShippingChart() {
  const { pages, toggle, mode } = useOrigin();
  const { byId, ships, cells, chars } = useChart(pages);
  const [hidden, setHidden] = useState([]);
  const visible = (a, b) => (cells[pairKey(a.id, b.id)] || []).filter((e) => !hidden.includes(shipLabel(e.ship)));

  return (
    <div className="px-4 sm:px-6 md:px-12 py-10 max-w-6xl 2xl:max-w-[1400px] space-y-6" data-testid="shipping-chart-page">
      <div>
        <p className="font-code text-xs uppercase tracking-[0.3em] text-pink-400/80 mb-2">Appendix · Pairings</p>
        <h1 className="font-display text-4xl sm:text-5xl text-amber-50">Shipping chart</h1>
        <p className="text-sm text-slate-500 mt-2">Every pairing in this world, at a glance. Tap a square for details.</p>
        {toggle && <div className="mt-4">{toggle}</div>}
      </div>
      {ships.length === 0 ? (mode !== "all" ? <Empty title={mode === "original" ? "No original ships yet." : "No ships from canon."} body="Switch the filter to All to see every pairing in this profile." /> : <Empty title="No ships yet." body="Pair two characters to start your shipping chart." />) :
        chars.length === 0 ? <Empty title="Nothing to chart yet." body="Link character pages as members of your ships to see them here." /> : (
        <>
          <Filters hidden={hidden} setHidden={setHidden} ships={ships} />
          <div className="w-fit max-w-full rounded-2xl border border-line bg-inset overflow-auto max-h-[75vh] shadow-[0_0_40px_-20px_rgba(236,72,153,0.4)]" data-testid="chart-scroll">
            <table className="border-separate border-spacing-0" data-testid="shipping-chart-grid">
              <thead>
                <tr>
                  <th className="sticky top-0 left-0 z-30 bg-inset border-b border-r border-line" />
                  {chars.map((c) => <ColHead key={c.id} c={c} />)}
                </tr>
              </thead>
              <tbody>
                {chars.map((a, ri) => (
                  <tr key={a.id}>
                    <RowHead c={a} />
                    {chars.map((b, ci) => a.id === b.id
                      ? <td key={b.id} className="border border-line bg-[repeating-linear-gradient(45deg,rgb(var(--c-panel))_0_4px,rgb(var(--c-inset))_4px_8px)]" />
                      : <ChartCell key={b.id} a={a} b={b} entries={visible(a, b)} byId={byId} delay={Math.min(ri + ci, 20) * 30} />)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
